import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import Tabs from '../components/common/Tabs.jsx';
import LineChart from '../components/charts/LineChart.jsx';
import TrendChart from '../components/charts/TrendChart.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import { StorageVolumeIcon } from '../components/icons.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, SavingCell, RecCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, parseSaving, formatSavingStr,
  buildBaseCostKpis, exportCSV, buildGroupedCostDonut, buildExportFilename, ADD_NEW_ACCOUNT,
  setScopedItem, STORAGE_KEYS, readDrilldownFilter,
} from '../components/common/Helper';
import MultiSelect from '../components/common/MultiSelect.jsx';

import OptimizationService from '../services/OptimizationService';
import useTagList from '../hooks/useTagList';
import { useAuth } from '../state/AuthContext';
import DrilldownNote from '../components/optimization/DrilldownNote.jsx';
import { useLocation, useNavigate } from 'react-router-dom';

const tabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'AWS Recommendations' },
];

const resolveEBSRec = (recType) => {
  const displayVal = recType ?? '';
  const t = displayVal.toUpperCase().trim();

  let recPillClass = 'rec-rs';
  if (t === 'OPTIMAL' || t === 'OPTIMIZED' || t === 'NONE' || t === 'NA' || t === '') {
    recPillClass = 'rec-ok';
  } else if (t === 'DELETE' || t === 'REMOVE' || t === 'UNATTACHED' || t === 'RELEASE' || t === 'UPSIZE') {
    recPillClass = 'rec-id';
  } else if (t === 'STOP' || t === 'STOPPED' || t === 'SCHEDULE') {
    recPillClass = 'rec-warn';
  }

  return {
    recType: displayVal,
    recPillClass
  };
};

const getVolumeName = (item) => {
  const tags = item.Tags ?? item.tags ?? [];
  const nameTag = tags.find((t) => (t.Key ?? t.key) === 'Name');
  return nameTag?.Value ?? nameTag?.value ?? item.volumeId ?? '—';
};

// ── Unified Row Mapper ─────────────────────────────────────
const mapEBSItem = (item, source) => {
  const recommendedField =
    source === 'custom'
      ? (item.customOnDemandMonthlyStoragePriceValue ?? item.recommendedOnDemandMonthlyStoragePriceValue)
      : item.recommendedOnDemandMonthlyStoragePriceValue;

  const currentField =
    source === 'custom'
      ? (item.currentOnDemandMonthlyStoragePriceValue ?? item.OnDemandMonthlyStoragePriceValue)
      : (item.currentOnDemandMonthlyStoragePriceValue || recommendedField);

  const current = parseCost(currentField);
  const recommended = parseCost(recommendedField);
  const saving = item.savingCost != null ? parseCost(item.savingCost) : Math.max(0, current - recommended);

  const rawRecType = source === 'aws'
    ? (item.finding ?? item.recommendationType ?? 'OPTIMAL')
    : (item.recommendationType ?? 'OPTIMAL');

  const awsRecTypeMap = { NotOptimized: 'DOWNSIZE', Optimized: 'OPTIMAL' };

  const recKey = source === 'aws' ? (awsRecTypeMap[rawRecType] ?? rawRecType) : rawRecType;
  const { recType, recPillClass } = resolveEBSRec(recKey);

  const remark = source === 'custom'
    ? (item.customRecomendationRemark ?? '')
    : (item.customRecomendationRemark && item.customRecomendationRemark.toUpperCase().trim() !== 'NA'
        ? item.customRecomendationRemark
        : (item.recommendationDetail ?? 'No recommendation'));

  const remarkUpper = remark.toUpperCase().trim();
  const recTypeUpper = recType.toUpperCase().trim();

  return {
    id: item.volumeId,
    name: getVolumeName(item),
    status: item.state === 'in-use' ? 'In-Use' : 'Available',
    type: item.volumeType ?? item.currentConfiguration?.volumeType ?? '—',
    region: item.region ?? '—',
    size: item.size ?? item.currentConfiguration?.volumeSize ?? null,
    iops: item.iops !== '' ? item.iops : (item.currentConfiguration?.volumeBaselineIOPS ?? null),
    iopsUsed: item.iopsUsed ?? null,
    iopsUtilization: item.iopsUtilizationPercent ?? null,
    attachmentInstanceIds: (() => {
      const raw = item.attachmentInstanceIds ?? item.attachmentIntanceIds;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string' && raw) return [raw];
      return [];
    })(),
    throughput: item.throughput !== '' ? item.throughput : (item.currentConfiguration?.volumeBaselineThroughput ?? null),
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: recTypeUpper === 'DELETE' ? '$0.00' : recommended > 0 ? `$${recommended.toFixed(2)}` : '—',
    recommendedCostValue: recTypeUpper === 'DELETE' ? 0 : recommended,
    savingValue: saving,
    recommendationRemark: source === 'custom'
      ? (remarkUpper !== '' && remarkUpper !== 'NA' ? remark : 'Optimal')
      : remark,
    recType,
    recPillClass,
    recLabel: source === 'aws'
      ? (item.recommendedVolumeType ? `→ ${item.recommendedVolumeType}` : '')
      : (item.recommendationDetail ?? ''),
    saving: formatSavingStr(saving),
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
    checked: false,
    tags: item.Tags ?? item.tags ?? [],
  };
};

// ── KPI Builder ────────────────────────────────────────────
const buildKpiData = (totalPriceValue, totalVolumes, rows = []) => {
  const current = parseCost(totalPriceValue?.currentCost);
  const recommended = parseCost(totalPriceValue?.recommendedCost);
  const saving = parseCost(totalPriceValue?.savingCost);
  const inUseCount = rows.filter((r) => r.state === 'in-use').length;
  const unattachedCount = rows.filter((r) => r.state === 'available').length;
  return buildBaseCostKpis({
    countTitle: 'Total Volumes',
    countValue: totalVolumes,
    countSubtitle: `${inUseCount} in-use · ${unattachedCount} unattached`,
    current, saving, recommended,
  });
};

// ── Chart Builders ─────────────────────────────────────────
const buildEbsTrendChartData = (rows) => {
  const typeColors = {
    gp2: { border: '#f57c00', bg: 'rgba(245,124,0,0.15)' },
    gp3: { border: '#2ea66f', bg: 'rgba(46,166,111,0.15)' },
    io1: { border: '#2196f3', bg: 'rgba(33,150,243,0.15)' },
    io2: { border: '#1565c0', bg: 'rgba(21,101,192,0.15)' },
    st1: { border: '#8e24aa', bg: 'rgba(142,36,170,0.15)' },
    sc1: { border: '#ec407a', bg: 'rgba(236,64,122,0.15)' },
  };
  const active = rows
    .map((r) => ({ id: r.id, name: r.name !== '—' ? r.name : r.id, type: r.type ?? 'other', cost: r.costValue ?? 0 }))
    .filter((r) => r.cost > 0)
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 15);
  if (!active.length) return null;
  const types = [...new Set(active.map((r) => r.type))];
  return {
    labels: active.map((r) => r.name),
    datasets: types.map((t) => {
      const c = typeColors[t] ?? { border: '#9aa3b7', bg: 'rgba(154,163,183,0.15)' };
      return { label: t, data: active.map((r) => r.type === t ? parseFloat(r.cost.toFixed(2)) : 0), borderColor: c.border, backgroundColor: c.bg, fill: true, tension: 0.3, borderWidth: 2, pointRadius: 3 };
    }),
  };
};

const buildIopsVsCostChartData = (rows) => {
  const active = rows
    .filter((r) => r.iopsUtilization !== null && r.size != null)
    .map((r) => ({ name: r.name !== '—' ? r.name : r.id, iopsUtil: r.iopsUtilization ?? 0, size: r.size ?? 0 }))
    .filter((r) => r.size > 0)
    .sort((a, b) => b.size - a.size)
    .slice(0, 20);
  if (!active.length) return null;
  return {
    labels: active.map((r) => r.name),
    datasets: [
      { label: 'IOPS Util %', data: active.map((r) => r.iopsUtil), backgroundColor: 'rgba(33,150,243,0.7)', borderColor: '#2196f3', borderWidth: 1, borderRadius: 4, yAxisID: 'y1' },
      { label: 'Size (GiB)', data: active.map((r) => r.size), backgroundColor: 'rgba(245,124,0,0.7)', borderColor: '#f57c00', borderWidth: 1, borderRadius: 4, yAxisID: 'y2' },
    ],
  };
};

const buildCostByVolumeTypeDonut = (rows) => {
  const typeColors = { gp2: '#f57c00', gp3: '#2ea66f', io1: '#2196f3', io2: '#1565c0', st1: '#8e24aa', sc1: '#ec407a' };
  const typeMap = {};
  rows.forEach((r) => { const t = r.type ?? 'other'; typeMap[t] = (typeMap[t] || 0) + (r.costValue ?? 0); });
  const sorted = Object.entries(typeMap).sort((a, b) => b[1] - a[1]);
  if (!sorted.reduce((s, [, v]) => s + v, 0)) return null;
  return {
    labels: sorted.map(([t]) => t),
    datasets: [{ data: sorted.map(([, v]) => parseFloat(v.toFixed(2))), backgroundColor: sorted.map(([t]) => typeColors[t] ?? '#9aa3b7'), borderRadius: 4, barPercentage: 0.6 }],
  };
};

const buildInsightActions = (rows) => {
  const seen = new Set();
  const withSaving = rows
    .filter((r) => {
      const key = `${r.id}|${r.recType}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return parseSaving(r.saving) > 0;
    })
    .map((r) => ({ ...r, savingNum: parseSaving(r.saving) }))
    .sort((a, b) => b.savingNum - a.savingNum);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingNum, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →';
    let actionClass = 'mini-btn';
    if (r.recType === 'DELETE') actionLabel = 'Delete →';
    else if (r.recType === 'DOWNSIZE') { actionLabel = 'Downsize →'; actionClass = 'mini-btn ghost'; }
    else if (r.recType === 'UPSIZE') { actionLabel = 'Upsize →'; actionClass = 'mini-btn ghost'; }
    const recLabelClean = r.recLabel ? r.recLabel.replace('→ ', '') : '';
    const descParts = [r.type + (r.size ? ` (${r.size} GB)` : ''), recLabelClean ? `→ ${recLabelClean}` : '', r.recommendationRemark].filter(Boolean);
    return { id: i + 1, title: r.name || r.id, amount: r.saving, desc: descParts.join(' · '), actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

// ── Table Columns (built inside component so handleDetailsClick is in scope) ──

// ── Main Page ──────────────────────────────────────────────
const EBSOptimization = () => {
  const { selectedAccount } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // A drill-down link (e.g. an EC2 instance's attached volumes) names both the
  // volume ids and the tab they were read from, so this page opens that tab and
  // filters that tab's own API data.
  const drilldown = useMemo(() => readDrilldownFilter(location.search), [location.search]);
  // Captured once: clearing the filter later must not yank the user's tab back.
  const initialTabRef = useRef(drilldown.tab != null && drilldown.tab < tabs.length ? drilldown.tab : 0);

  const [activeTab, setActiveTab] = useState(initialTabRef.current);
  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [isLoadingAWS, setIsLoadingAWS] = useState(false);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData, setAiData] = useState(null);

  const handleDetailsClick = (row) => {
    setAiData({
      volumeId: row.id,
      volumeType: row.type,
      region: row.region,
      size: row.size != null ? `${row.size} GiB` : '—',
      status: row.status,
      iopsUtilization: row.iopsUtilization != null ? `${row.iopsUtilization}%` : '—',
      currentMonthlyCost: row.cost,
      recommendedCost: row.recommendedCost,
      recommendation: row.recType,
      recommendationDetail: row.recommendationRemark,
      estimatedSaving: row.saving,
    });
    setAiPrompt('You are a cloud cost advisor. Based on this EBS volume data, give a short, friendly, and natural insight about the storage cost situation, the recommended action, and potential monthly savings.');
    setAiOpen(true);
  };

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [awsRows, setAwsRows] = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi, setCustomKpi] = useState([]);
  const [awsKpi, setAwsKpi] = useState([]);

  const [byAZData, setByAZData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [iopsData, setIopsData] = useState(null);
  const [typeDonutData, setTypeDonutData] = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal, setInsightTotal] = useState(0);

  const { tagList } = useTagList('EBS');
  const [filterList, setFilterList] = useState({ tag: '', values: [] });

  const fetchedTabsRef = useRef(new Set());

  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (tabIndex === 0 && isLoadingCombined) return;
    if (tabIndex === 1 && isLoadingCustom) return;
    if (tabIndex === 2 && isLoadingAWS) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    if (tabIndex === 0) {
      setIsLoadingCombined(true);
      setCombinedRows([]);
      OptimizationService.getTabularData(selectedAccount, 'COMBINED_EBS', null, isForceRefresh)
        .then((data) => {
          setCombinedRows((data?.rows ?? []).map((item) => mapEBSItem(item, 'combined')));
          setCombinedKpi(buildKpiData(data?.totalPriceValue, data?.rows?.length ?? 0, data?.rows ?? []));
        })
        .finally(() => setIsLoadingCombined(false));
    } else if (tabIndex === 1) {
      setIsLoadingCustom(true);
      setCustomRows([]);
      OptimizationService.getTabularData(selectedAccount, 'CUSTOM_EBS', null, isForceRefresh)
        .then((data) => {
          setCustomRows((data?.rows ?? []).map((item) => mapEBSItem(item, 'custom')));
          setCustomKpi(buildKpiData(data?.totalPriceValue, data?.rows?.length ?? 0, data?.rows ?? []));
        })
        .finally(() => setIsLoadingCustom(false));
    } else {
      setIsLoadingAWS(true);
      setAwsRows([]);
      OptimizationService.getTabularData(selectedAccount, 'EBS', null, isForceRefresh)
        .then((data) => {
          setAwsRows((data?.rows ?? []).map((item) => mapEBSItem(item, 'aws')));
          setAwsKpi(buildKpiData(data?.totalPriceValue, data?.rows?.length ?? 0, data?.rows ?? []));
        })
        .finally(() => setIsLoadingAWS(false));
    }
  };

  const handleTabChange = (i) => { setActiveTab(i); activeTabRef.current = i; fetchTabData(i); };

  // Mirrors activeTab for the account effect below, which must refetch whichever
  // tab is on screen - the drill-down link can land the user on any of them.
  const activeTabRef = useRef(initialTabRef.current);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) { setFilterList({ tag: '', values: [] }); fetchedTabsRef.current = new Set(); fetchTabData(activeTabRef.current); } }, [selectedAccount]);

  const filteredCustomRows = useMemo(() => {
    if (!filterList.tag || filterList.values.length === 0) return customRows;
    return customRows.filter((row) => {
      const tags = row.tags ?? [];
      return tags.some((t) => (t.Key ?? t.key) === filterList.tag && filterList.values.includes(t.Value ?? t.value));
    });
  }, [customRows, filterList]);

  useEffect(() => {
    const rows = activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows;
    if (rows.length > 0) {
      setByAZData(buildGroupedCostDonut(rows, 'region'));
      setTrendData(buildEbsTrendChartData(rows));
      setIopsData(buildIopsVsCostChartData(rows));
      setTypeDonutData(buildCostByVolumeTypeDonut(rows));
      const { actions, totalSaving } = buildInsightActions(rows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setByAZData(null); setTrendData(null); setIopsData(null); setTypeDonutData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [activeTab, combinedRows, filteredCustomRows, awsRows]);

  const activeKpi = activeTab === 0 ? combinedKpi : activeTab === 1 ? customKpi : awsKpi;
  const activeRows = activeTab === 0 ? combinedRows : activeTab === 1 ? filteredCustomRows : awsRows;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAWS;

  // The drill-down narrows the table only. KPIs, charts and the header badge
  // still describe the whole account, so the filtered rows read as a subset of
  // the full dataset rather than as a different dataset.
  const tableRows = useMemo(
    () => (drilldown.isActive ? activeRows.filter((row) => drilldown.idSet.has(row.id)) : activeRows),
    [activeRows, drilldown],
  );
  const clearDrilldown = () => navigate(location.pathname, { replace: true });

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'EBS Volume Optimization',
      kpi: activeKpi,
      ebsRecommendations: activeRows,
    });
  }, [activeTab, combinedRows, filteredCustomRows, awsRows]);

  const attachmentCol = {
    key: 'attachmentInstanceIds',
    label: 'Attached Instance',
    render: (row) => row.attachmentInstanceIds?.length > 0
      ? row.attachmentInstanceIds.map((id, i) => <div key={i}>{id}</div>)
      : <span style={{ color: 'var(--muted-2)' }}>— (unattached)</span>,
  };

  const baseColumns = [
    { key: 'id', label: 'Volume ID' },
    { key: 'status', label: 'Status', render: (row) => <span className={`status-pill ${row.status === 'In-Use' ? 'st-running' : 'st-stopped'}`}>{row.status}</span> },
    { key: 'type', label: 'Volume Type' },
    { key: 'region', label: 'Region' },
    { key: 'size', label: 'Size (GB)', render: (row) => row.size != null ? `${row.size} GB` : '—' },
    { key: 'iopsUtilization', label: 'IOPS Utilization', render: (row) => (row.iopsUsed != null && row.iops != null) ? `${row.iopsUsed} / ${row.iops}` : '—' },
    { key: 'cost', label: 'Cost / Mo', className: 'num', render: (row) => <CostCell row={row} /> },
    { key: 'recommendationRemark', label: 'Recommendation', render: (row) => <RecCell row={row} /> },
    { key: 'saving', label: 'Saving / Mo', className: 'num', render: (row) => <SavingCell row={row} /> },
    { key: 'action', label: 'Action', render: (row) => <button className={row.actionClass} onClick={() => handleDetailsClick(row)}>{row.actionLabel}</button> },
  ];

  const ebsColumns = [baseColumns[0], attachmentCol, ...baseColumns.slice(1)];

  const handleExport = () =>
    exportCSV(
      tableRows,
      ebsColumns.filter((c) => !['iopsUtilization', 'action'].includes(c.key) && typeof c.label === 'string'),
      buildExportFilename(
        `ebs_volume_optimization_${['combined', 'cloudsanalytics', 'aws'][activeTab] ?? activeTab}${drilldown.isActive ? '_filtered' : ''}`,
        selectedAccount,
      ),
    );

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>EBS Volume</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            EBS Volume Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{activeRows.length} volumes</span>}
          </h1>
          <div className="meta">View, rightsize, delete, and act on recommendations across all EBS volumes</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={tableRows.length === 0}>⬇ Export</button>
          <button className="btn ghost" onClick={() => fetchTabData(activeTab, true)} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <KpiRow
        kpiData={activeKpi}
        loading={isLoadingActive}
        iconOverrides={{ t1: <StorageVolumeIcon /> }}
      />

      <div className="grid-3" style={{ marginTop: '14px' }}>
        <Card className="first" title="Volume Size vs IOPS Utilization" subtitle="IOPS util % vs size (GiB) · top 20 by size">
          <LineChart data={iopsData} loading={isLoadingActive} y1Label="IOPS Util %" y2Label="Size (GiB)" y1Suffix="%" y2Prefix="" />
        </Card>
        <Card className="first" title="Cost by Volume Type" subtitle="Monthly cost breakdown by EBS volume type">
          <DonutChart data={typeDonutData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard title="Top EBS actions" actions={insightActions} total={insightTotal} loading={isLoadingActive} entityName="volumes" onAction={(item) => item.row && handleDetailsClick(item.row)} />
      </div>

      <Tabs tabs={tabs} defaultActive={initialTabRef.current} onTabChange={handleTabChange} />

      {activeTab === 1 && (
        <div className="filter-bar">
          <div className="field">
            <label>Filter Tag</label>
            <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
              <option value="">None</option>
              {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Filter Tag Values</label>
            <MultiSelect
              options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
              value={filterList.values}
              onChange={(vals) => handleFilterChange('values', vals)}
              disabled={!filterList.tag}
            />
          </div>
          <div className="spacer" />
          <button className="btn ghost" onClick={() => setFilterList({ tag: '', values: [] })}>✕ Clear</button>
          <button className="btn primary" onClick={() => fetchTabData(activeTab, true)} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      )}

      {drilldown.isActive && (
        <DrilldownNote
          entityName="volumes"
          sourceEntityName="instance"
          sourceLabel={drilldown.sourceLabel}
          sourceId={drilldown.sourceId}
          tabLabel={tabs[activeTab]?.label}
          requestedCount={drilldown.ids.length}
          shownCount={tableRows.length}
          totalCount={activeRows.length}
          loading={isLoadingActive}
          onClear={clearDrilldown}
        />
      )}

      <Table
        rows={tableRows}
        columns={ebsColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText={drilldown.isActive
          ? 'None of the linked volumes are in this tab - use "Show all volumes" above, or try another tab.'
          : 'No EBS volumes found.'}
        subscriptionGated
      />

      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="EBS Volume Cost Distribution" subtitle="Monthly spend by volume type (Top 15 volumes)">
          <TrendChart data={trendData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Cost by Region" subtitle="Active EBS regions">
          <DonutChart data={byAZData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default EBSOptimization;
